import React, { useState, useEffect } from "react";
import "../style/css/TelegramBot.css";
import api from "../api.js";
// TypeScript interfaces
interface TelegramBotSettings {
  morningReminder: boolean;
  oneHourReminder: boolean;
  updateAlerts: boolean;
}

interface TelegramBotData {
  connected: boolean;
  botUsername: string;
  telegramLink: string;
  settings: TelegramBotSettings;
}

interface EditModalProps {
  isOpen: boolean;
  onClose: () => void;
  settings: TelegramBotSettings;
  onSave: (settings: TelegramBotSettings) => void;
  botUsername: string;
}

// SVG Icons
const BotIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M12 2C8.13 2 5 5.13 5 9c0 1.74.5 3.37 1.41 4.84.91 1.47 2.02 2.84 2.33 3.41.31.57.59 1.03.87 1.38.28.35.59.66 1 .88.41.22.87.34 1.39.34.52 0 .98-.12 1.39-.34.41-.22.72-.53 1-.88.28-.35.56-.81.87-1.38.31-.57 1.42-1.94 2.33-3.41C18.5 12.37 19 10.74 19 9c0-3.87-3.13-7-7-7z" />
    <circle cx="9" cy="9" r="1" />
    <circle cx="15" cy="9" r="1" />
  </svg>
);

const ConnectedIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M5 12l5 5L20 7" />
  </svg>
);

const DisconnectedIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const MorningIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <path d="M12 2v2M12 20v2M4 12H2M22 12h-2M5.64 5.64l1.42 1.42M16.94 16.94l1.42 1.42M5.64 18.36l1.42-1.42M16.94 7.06l1.42-1.42M12 8a4 4 0 100 8 4 4 0 000-8z" />
  </svg>
);

const ClockIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l4 2" />
  </svg>
);

const AlertIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 9v4M12 17h.01" />
  </svg>
);

const EditIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const TelegramIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.57-1.38-.93-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.06-.2-.07-.06-.17-.04-.24-.02-.1.02-1.79 1.14-5.06 3.34-.48.33-.91.49-1.3.48-.43-.01-1.26-.24-1.88-.44-.76-.24-1.36-.37-1.31-.78.03-.26.32-.53.88-.81 3.47-1.51 5.78-2.51 6.94-3.01 3.05-1.32 3.68-1.55 4.1-1.56.09 0 .3.02.43.14.11.1.14.23.16.43.02.2.03.66.01 1.08z" />
  </svg>
);

// Edit Modal Component
const EditModal: React.FC<EditModalProps> = ({
  isOpen,
  onClose,
  settings,
  onSave,
  botUsername,
}) => {
  const [formSettings, setFormSettings] =
    useState<TelegramBotSettings>(settings);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formSettings);
    onClose();
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>Editar Configuración</h3>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="bot-info">
          <div className="bot-info-icon">
            <BotIcon />
          </div>
          <div>
            <h4>@{botUsername}</h4>
            <p className="text-muted">
              Configura las notificaciones de tu asistente virtual
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formSettings.morningReminder}
                onChange={(e) =>
                  setFormSettings((prev) => ({
                    ...prev,
                    morningReminder: e.target.checked,
                  }))
                }
              />
              <span className="checkbox-custom"></span>
              <div className="checkbox-text">
                <div className="checkbox-title">
                  <MorningIcon />
                  <span>Recordatorio matutino</span>
                </div>
                <p className="checkbox-description">
                  Recibe un resumen diario por la mañana
                </p>
              </div>
            </label>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formSettings.oneHourReminder}
                onChange={(e) =>
                  setFormSettings((prev) => ({
                    ...prev,
                    oneHourReminder: e.target.checked,
                  }))
                }
              />
              <span className="checkbox-custom"></span>
              <div className="checkbox-text">
                <div className="checkbox-title">
                  <ClockIcon />
                  <span>Recordatorio 1 hora antes</span>
                </div>
                <p className="checkbox-description">
                  Te avisamos 1 hora antes de tus eventos
                </p>
              </div>
            </label>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formSettings.updateAlerts}
                onChange={(e) =>
                  setFormSettings((prev) => ({
                    ...prev,
                    updateAlerts: e.target.checked,
                  }))
                }
              />
              <span className="checkbox-custom"></span>
              <div className="checkbox-text">
                <div className="checkbox-title">
                  <AlertIcon />
                  <span>Alertas de actualizaciones</span>
                </div>
                <p className="checkbox-description">
                  Recibe notificaciones sobre nuevas funciones
                </p>
              </div>
            </label>
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn btn--secondary"
              onClick={onClose}
            >
              Cancelar
            </button>
            <button type="submit" className="btn btn--primary">
              Guardar cambios
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Main Component
const TelegramBot: React.FC = () => {
  const [botData, setBotData] = useState<TelegramBotData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchBotData();
  }, []);

  const fetchBotData = async () => {
    try {
      setLoading(true);
      // This would be your actual API call
      // const response = await fetch('/profiles/telegram_bot/');
      // const data = await response.json();

      // Mock data for demonstration
      const response = api.get("/profiles/profile_telegram_bot/");
      const data: TelegramBotData = (await response).data;
      setBotData(data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const handleConnect = () => {
    if (botData?.telegramLink) {
      window.open(botData.telegramLink, "_blank", "noopener,noreferrer");
    }
  };

  const handleSaveSettings = (newSettings: TelegramBotSettings) => {
    // Here you would make an API call to save the settings
    setBotData((prev) => (prev ? { ...prev, settings: newSettings } : null));
  };

  if (loading) {
    return (
      <div className="telegram-bot-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Cargando configuración del bot...</p>
        </div>
      </div>
    );
  }

  if (!botData) {
    return (
      <div className="telegram-bot-container">
        <div className="error-state">
          <DisconnectedIcon />
          <p>Error al cargar los datos del bot</p>
          <button className="btn btn--primary" onClick={fetchBotData}>
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="telegram-bot-container">
      <div className="telegram-bot-card">
        <div className="bot-header">
          <div className="bot-icon-wrapper">
            <TelegramIcon />
          </div>
          <div>
            <h2>Asistente de Telegram</h2>
            <div className="status-badge">
              {botData.connected ? (
                <>
                  <ConnectedIcon />
                  <span className="status-connected">
                    Conectado a @{botData.botUsername}
                  </span>
                </>
              ) : (
                <>
                  <DisconnectedIcon />
                  <span className="status-disconnected">No conectado</span>
                </>
              )}
            </div>
          </div>
        </div>

        {!botData.connected ? (
          <div className="connection-prompt">
            <p className="prompt-text">
              Conecta tu cuenta de Telegram para recibir recordatorios y
              actualizaciones directamente en tu teléfono.
            </p>
            <button
              className="btn btn--primary btn--large"
              onClick={handleConnect}
            >
              <TelegramIcon />
              Conectar a Telegram
            </button>
            <p className="help-text">
              Al hacer clic se abrirá Telegram. Envía /start al bot para
              completar la conexión.
            </p>
          </div>
        ) : (
          <div className="bot-settings">
            <div className="settings-header">
              <h3>Configuración de notificaciones</h3>
              <button
                className="btn btn--secondary btn--small"
                onClick={() => setIsModalOpen(true)}
              >
                <EditIcon />
                Editar configuración
              </button>
            </div>

            <div className="settings-list">
              <div
                className={`setting-item ${
                  botData.settings.morningReminder ? "active" : "inactive"
                }`}
              >
                <div className="setting-icon">
                  <MorningIcon />
                </div>
                <div className="setting-content">
                  <h4>Recordatorio matutino</h4>
                  <p>Recibe un resumen diario por la mañana</p>
                </div>
                <div className="setting-status">
                  <span
                    className={`status-dot ${
                      botData.settings.morningReminder ? "on" : "off"
                    }`}
                  ></span>
                  {botData.settings.morningReminder
                    ? "Activado"
                    : "Desactivado"}
                </div>
              </div>

              <div
                className={`setting-item ${
                  botData.settings.oneHourReminder ? "active" : "inactive"
                }`}
              >
                <div className="setting-icon">
                  <ClockIcon />
                </div>
                <div className="setting-content">
                  <h4>Recordatorio 1 hora antes</h4>
                  <p>Te avisamos 1 hora antes de tus eventos</p>
                </div>
                <div className="setting-status">
                  <span
                    className={`status-dot ${
                      botData.settings.oneHourReminder ? "on" : "off"
                    }`}
                  ></span>
                  {botData.settings.oneHourReminder
                    ? "Activado"
                    : "Desactivado"}
                </div>
              </div>

              <div
                className={`setting-item ${
                  botData.settings.updateAlerts ? "active" : "inactive"
                }`}
              >
                <div className="setting-icon">
                  <AlertIcon />
                </div>
                <div className="setting-content">
                  <h4>Alertas de actualizaciones</h4>
                  <p>Recibe notificaciones sobre nuevas funciones</p>
                </div>
                <div className="setting-status">
                  <span
                    className={`status-dot ${
                      botData.settings.updateAlerts ? "on" : "off"
                    }`}
                  ></span>
                  {botData.settings.updateAlerts ? "Activado" : "Desactivado"}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <EditModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        settings={botData.settings}
        onSave={handleSaveSettings}
        botUsername={botData.botUsername}
      />
    </div>
  );
};

export default TelegramBot;
